/**
 * Кейсы на главной — одна лента коротких карточек: сектор · масштаб · две
 * цифры. Первыми идут живые внедрения ИИ (страница продаёт ИИ, а на
 * телефоне до седьмой карточки не долистывают), за ними шесть кейсов до
 * эпохи ИИ. Полный разбор каждого кейса — на своей странице /keysy/<slug>
 * в едином формате (src/pages/case-study.tsx); карточка ведёт туда.
 * Проекты в работе живут на хабе /keysy.
 *
 * Под лентой — отзывы (#reviews, адрес /reviews): три дословные цитаты из
 * трекера ОДНОГО внедрения — второго кейса ленты. Раньше они стояли
 * отдельным блоком через четыре секции тремя равноправными карточками под
 * заголовком во множественном числе и читались как «три клиента»; здесь
 * они подписаны честно — один кейс, три голоса. Ряд видео-карточек и крупное
 * «60+» из старого блока сюда не переехали: видео-отзыв уже есть в карточке
 * каждого кейса, а «60+» — про кейсы до эпохи ИИ и рядом с ИИ-отзывами
 * вводило в заблуждение (конверсионный аудит 2026-09-21).
 *
 * Разметки Review/AggregateRating нет намеренно: отзыв без имени автора
 * невалиден, а имя нарушает NDA.
 *
 * i18n: у страниц кейсов есть английская версия /en/keysy/<slug>, поэтому
 * «Разбор» есть в обеих локалях. Видео-отзывы — на русском, на /en помечены
 * «(RU)». ИИ-кейсы под NDA — только сектор + масштаб в обеих локалях.
 */

import { Sparkline } from '@/components/Sparkline'
import { CardScroller, SCROLL_ITEM } from '@/components/CardScroller'
import { NavLink } from '@/components/NavLink'
import { track } from '@/lib/analytics'
import { localizedPath, useLocale } from '@/i18n/locale'
import { casePath } from '@/lib/case-slugs'
import {
  PRE_AI,
  HOME_CASE_ORDER,
  CASE_CARDS,
  CASES_COPY,
  CASES_HOME_COPY,
  CASE_CARD_COPY,
  type HomeCaseId,
} from '@/data/cases'
import './cases.css'

const LABEL = 'font-mono text-small uppercase tracking-wider'

/**
 * Прямая речь — дословно, из трекера проекта (бывшая секция Reviews, до неё —
 * сцены живого внедрения InsideScenes). Все три голоса — из внедрения
 * «Производство и торговля · Нефтегаз» (CASE_CARDS['neftegaz-trade']),
 * поэтому подпись у карточки — только роль: компания названа один раз, над
 * рядом. Порядок хронологический: перенос учёта → помощник менеджера →
 * собственник на третьей неделе.
 */
const VOICES = {
  ru: {
    kicker: 'Отзывы · дословно, из трекера проекта',
    heading: 'Три голоса из одного внедрения',
    note: 'Производство и торговля · Нефтегаз — его карточка в ленте выше. Проект под NDA, поэтому вместо имени — сектор и масштаб.',
    quotes: [
      {
        text: 'Мы расслабляемся: ничего не вносить, таблички не открывать. Я даже не знаю, где таблички у меня находятся. Говорю: занеси — он это всё куда-то разносит, таблички создаёт, но я сам в них не захожу. Я смотрю просто, как работает.',
        who: 'Собственник · третья неделя',
      },
      {
        text: 'Я с помощью него создал новый сервер — он сделал практически всё за меня. Я выгрузил 1С, перенёс — он всё отрегулировал внутри, все цифры проверил. Бухгалтер проверила — всё состыковалось полностью. Сегодня у нас полный переход со старого сервера, который стоял дома на компьютере, в облако с грамотно выстроенной безопасностью.',
        who: 'Собственник',
      },
      {
        text: 'Он мне пишет возможные действия и какие могут быть результаты. Смотрит всех, кто не задействован: выискивает заброшенных, отложенных, подвисших клиентов, где нет вообще коммуникации.',
        who: 'Менеджер',
      },
    ],
    open: '«',
    close: '»',
    label: 'Отзывы клиентов о внедрении ИИ',
    prev: 'Предыдущие отзывы',
    next: 'Следующие отзывы',
    swipeHint: 'Листайте отзывы вбок',
  },
  en: {
    kicker: 'Reviews · verbatim, from the project tracker',
    heading: 'Three voices from one implementation',
    note: 'Manufacturing & trade · Oil and gas — its card is in the row above. The project runs under NDA, so instead of a name you see the sector and scale.',
    quotes: [
      {
        text: 'We’re relaxing: nothing to enter, no spreadsheets to open. I don’t even know where my spreadsheets are. I say: log it — it sorts it all somewhere, creates the tables, but I don’t go into them. I just watch it work.',
        who: 'Owner · week three',
      },
      {
        text: 'With its help I created a new server — it did practically everything for me. I exported the accounting system, moved it over — it tuned everything inside, checked all the numbers. The accountant checked — everything matched completely. Today we fully moved from the old server that sat on a home computer into the cloud with properly built security.',
        who: 'Owner',
      },
      {
        text: 'It writes me possible actions and likely outcomes. It looks at everyone left unattended: digs up abandoned, postponed, stuck clients with no communication at all.',
        who: 'Manager',
      },
    ],
    open: '“',
    close: '”',
    label: 'Client reviews of AI implementation',
    prev: 'Previous reviews',
    next: 'Next reviews',
    swipeHint: 'Swipe the reviews sideways',
  },
} as const

export function Cases() {
  const locale = useLocale()
  const t = CASES_HOME_COPY[locale]
  const shared = CASES_COPY[locale]
  const v = VOICES[locale]
  return (
    <section id="cases" className="section">
      <div className="container-x">
        <div className="max-w-3xl">
          <p className={`${LABEL} text-muted mb-5`}>{t.eyebrow}</p>
          <h2 className="text-h2 text-ink">{shared.heading}</h2>
          <p className="mt-6 text-body text-muted leading-relaxed">{t.lede}</p>
        </div>

        {/* На телефоне стрелок нет — подсказка остаётся единственным знаком,
            что ленту листают вбок (мандат Тони 2026-08-03). */}
        <p className={`mt-10 sm:hidden ${LABEL} text-muted flex items-center gap-2`}>
          <span aria-hidden>→</span> {shared.swipeHint}
        </p>
        <CardScroller label={t.srAll} prevLabel={shared.prev} nextLabel={shared.next} className="mt-4 sm:mt-10">
          {HOME_CASE_ORDER.map((id) => (
            <CaseCard key={id} id={id} />
          ))}
        </CardScroller>

        {/* id="reviews" — адрес /reviews (section-paths) и замер has_id_reviews;
            scroll-mt — чтобы прокрутка по адресу не прятала заголовок под шапку. */}
        <div id="reviews" className="scroll-mt-24 mt-8">
          <p className={`${LABEL} text-muted`}>{v.kicker}</p>
          <div className="mt-3 flex flex-wrap items-baseline justify-between gap-x-8 gap-y-1">
            <h3 className="text-h3 text-ink">{v.heading}</h3>
            <p className="text-small text-muted max-w-2xl">{v.note}</p>
          </div>

          <p className={`mt-5 sm:hidden ${LABEL} text-muted flex items-center gap-2`}>
            <span aria-hidden>→</span> {v.swipeHint}
          </p>
          {/* Та же лента, что у кейсов; на десктопе cases.css раскладывает её
              сеткой 1×3 — три голоса видны сразу, листать нечего. */}
          <CardScroller label={v.label} prevLabel={v.prev} nextLabel={v.next} className="cases-voices mt-4 sm:mt-6">
            {v.quotes.map((q) => (
              <figure key={q.text} className={`card flex flex-col ${SCROLL_ITEM}`}>
                {/* text-base, не text-body: на десктопе 18px в узкой колонке
                    раскладывали длинную цитату на 11 строк, и ряд из трёх
                    голосов вырастал до половины экрана. */}
                <blockquote className="flex-1 text-base text-ink leading-relaxed">
                  {v.open}
                  {q.text}
                  {v.close}
                </blockquote>
                <figcaption className="mt-4 pt-3 border-t border-divider text-small font-medium text-ink">
                  {q.who}
                </figcaption>
              </figure>
            ))}
          </CardScroller>
        </div>
      </div>
    </section>
  )
}

function CaseCard({ id }: { id: HomeCaseId }) {
  const locale = useLocale()
  const t = CASES_HOME_COPY[locale]
  const cardCopy = CASE_CARD_COPY[locale]
  const c = CASE_CARDS[id][locale]
  const pre = PRE_AI.find((p) => p.id === id)
  const trendLabel = pre?.[locale].trendLabel

  return (
    <article className={`card relative flex flex-col ${SCROLL_ITEM}`}>
      <div className="flex items-center gap-2">
        {/* Акцент — только метка данных: точка отличает живое внедрение
            от архивного кейса. */}
        <span
          aria-hidden
          className={`h-1.5 w-1.5 rounded-full ${pre ? 'bg-muted/50' : 'bg-accent cases-live-dot'}`}
        />
        <span className={`${LABEL} ${pre ? 'text-muted' : 'text-ink'}`}>{pre ? t.chipPre : t.chipLive}</span>
      </div>

      <h3 className="mt-6 text-h3 text-ink">{c.title}</h3>
      {/* Сектор и масштаб — всегда две строки: при одной общей строке
          перенос случался не у всех, и цифры соседних карточек стояли
          на разной высоте. */}
      <p className="mt-2 text-small text-ink leading-snug">{c.sector}</p>
      <p className="mt-1 text-small text-muted leading-snug min-h-[2.75em]">{c.scale}</p>

      {/* Полоса фиксированной высоты и у карточек без графика: иначе цифры
          в соседних карточках стоят на разной высоте и лента «пляшет». */}
      <div className="mt-6 h-12 flex items-end">
        {pre?.trendPoints && trendLabel ? (
          <Sparkline points={pre.trendPoints} label={trendLabel} className="text-accent" width={170} height={48} />
        ) : (
          <div aria-hidden className="w-full border-b border-dashed border-divider" />
        )}
      </div>

      <dl className="mt-6 grid grid-cols-2 gap-x-5 border-t border-divider pt-5 flex-1">
        {c.figures.map((f) => (
          <div key={f.value + f.label} className="flex flex-col-reverse justify-end">
            <dt className="mt-2 text-small text-muted leading-snug">{f.label}</dt>
            <dd className="cases-figure font-mono font-semibold text-ink tabular-nums">{f.value}</dd>
          </div>
        ))}
      </dl>

      <div className="mt-7 flex flex-wrap items-center justify-between gap-x-5 gap-y-2">
          <NavLink
            href={localizedPath(casePath(id), locale)}
            ariaLabel={t.openAria(c.title)}
            onClick={() => track('case_open', { slug: id })}
            className="stretched-link text-small font-medium text-ink no-underline hover:underline underline-offset-4"
          >
            {t.open}
          </NavLink>
          {pre?.videoUrl && (
            // relative z-10: иначе ссылку накрывает ::after растянутой
            // ссылки «Разбор», и клик по видео уводит на /keysy.
            <a
              href={pre.videoUrl}
              target="_blank"
              rel="noreferrer"
              onClick={() => track('review_video_click', { client: id })}
              className="relative z-10 inline-flex items-center gap-2 text-small text-muted no-underline hover:text-ink transition-colors"
              aria-label={cardCopy.videoAria(c.title)}
            >
              <svg width="10" height="10" viewBox="0 0 14 14" fill="currentColor" aria-hidden="true">
                <path d="M3 2l9 5-9 5V2z" />
              </svg>
              {cardCopy.video}
            </a>
          )}
        </div>
    </article>
  )
}
